import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Braces,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Copy,
  RotateCcw,
  FileText,
  Upload,
  File,
  CheckCircle2,
  AlertCircle,
  LayoutGrid,
  List,
  ChevronLeft,
  ChevronRight,
  Search,
  Lock,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { mergeKeyApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { generateUUID } from "@/utils/transform";

// Merge key card component for grid view
const MergeKeyCard = ({ mergeKey, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const keyValue = mergeKey.key || mergeKey.keyPattern || "";

  const fieldTypeLabels = {
    signature: t("mergeKeys.types.signature"),
    initials: t("mergeKeys.types.initials"),
    date: t("mergeKeys.types.date"),
    text: t("mergeKeys.types.text"),
    name: t("mergeKeys.types.name"),
    email: t("mergeKeys.types.email"),
    checkbox: t("mergeKeys.types.checkbox"),
  };

  const copyToClipboard = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`{{${keyValue}}}`);
    toast.success(t("mergeKeys.copied"));
  };

  return (
    <Card className="group hover:shadow-md transition-shadow">
      {/* Preview area */}
      <div className="h-32 bg-muted flex items-center justify-center relative">
        <div className="text-center">
          <Braces className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <code className="font-mono text-sm bg-background px-2 py-0.5 rounded">
            {`{{${keyValue}}}`}
          </code>
        </div>

        {/* Quick actions overlay */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={copyToClipboard}>
                <Copy className="mr-2 h-4 w-4" />
                {t("common.copy")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(mergeKey)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t("common.edit")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete(mergeKey)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("common.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Content */}
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {mergeKey.fieldType && (
            <Badge variant="outline">
              {fieldTypeLabels[mergeKey.fieldType] || mergeKey.fieldType}
            </Badge>
          )}
        </div>

        {mergeKey.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
            {mergeKey.description}
          </p>
        )}

        {mergeKey.defaultValue && (
          <p className="text-xs text-muted-foreground">
            Default:{" "}
            <span className="font-medium">{mergeKey.defaultValue}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Merge key list row component
const MergeKeyListRow = ({ mergeKey, onEdit, onDelete }) => {
  const { t } = useTranslation();
  // Handle both `key` and `keyPattern` field names
  const keyValue = mergeKey.key || mergeKey.keyPattern || "";

  const fieldTypeLabels = {
    signature: t("mergeKeys.types.signature"),
    initials: t("mergeKeys.types.initials"),
    date: t("mergeKeys.types.date"),
    text: t("mergeKeys.types.text"),
    name: t("mergeKeys.types.name"),
    email: t("mergeKeys.types.email"),
    checkbox: t("mergeKeys.types.checkbox"),
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`{{${keyValue}}}`);
    toast.success(t("mergeKeys.copied"));
  };

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Braces className="h-4 w-4 text-primary" />
          </div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm bg-muted px-2 py-0.5 rounded">
              {`{{${keyValue}}}`}
            </code>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={copyToClipboard}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </TableCell>
      <TableCell>
        {mergeKey.fieldType && (
          <Badge variant="outline">
            {fieldTypeLabels[mergeKey.fieldType] || mergeKey.fieldType}
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm max-w-50 truncate">
        {mergeKey.description || "-"}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {mergeKey.defaultValue || "-"}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(mergeKey)}>
              <Pencil className="mr-2 h-4 w-4" />
              {t("common.edit")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(mergeKey)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("common.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

// Loading skeleton
function MergeKeysSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>
      <Card>
        <CardContent className="p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// Empty state
const EmptyState = ({ onCreateClick, hasSearch }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <Braces className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-1">
          {hasSearch ? t("mergeKeys.noSearchResults") : t("mergeKeys.noKeys")}
        </h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {hasSearch
            ? t("mergeKeys.tryDifferentSearch")
            : t("mergeKeys.noKeysDesc")}
        </p>
        {!hasSearch && (
          <Button onClick={onCreateClick}>
            <Plus className="mr-2 h-4 w-4" />
            {t("mergeKeys.createKey")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Pagination component (matching Envelopes.jsx)
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    const endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        {t("common.page")} {currentPage} {t("common.of")} {totalPages}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t("common.previous")}
        </Button>
        <div className="flex gap-1">
          {getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? "default" : "outline"}
              size="sm"
              className="w-8 h-8 p-0"
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          {t("common.next")}
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// UpgradeRequired Component
// ============================================
const UpgradeRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold font-heading tracking-tight">
          {t("mergeKeys.title", "Merge Keys")}
        </h1>
        <p className="text-muted-foreground">
          {t(
            "mergeKeys.subtitle",
            "Define dynamic placeholders for your documents",
          )}
        </p>
      </div>

      {/* Single Card with divider */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {t("upgrade.featureNotAvailable", "Feature Not Available")}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t(
              "upgrade.mergeKeysUpgradeDesc",
              "Merge Keys allow you to create dynamic placeholders that automatically populate with recipient data.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/settings/billing")}>
              <Sparkles className="h-4 w-4 mr-2" />
              {t("upgrade.upgradePlan", "Upgrade Plan")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/envelopes")}>
              {t("common.backToEnvelopes", "Back to Envelopes")}
            </Button>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-8 border-t w-full max-w-lg">
            <div className="w-full flex items-center justify-center gap-2 mb-4">
              <Braces className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground">
                {t("mergeKeys.withMergeKeysYouCan", "With Merge Keys you can:")}
              </h3>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 flex items-center justify-center flex-col">
              <li>
                {t(
                  "mergeKeys.feature1",
                  "Create dynamic placeholders for documents",
                )}
              </li>
              <li>
                {t(
                  "mergeKeys.feature2",
                  "Auto-populate fields with recipient data",
                )}
              </li>
              <li>
                {t(
                  "mergeKeys.feature3",
                  "Test and preview merge key replacements",
                )}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const MergeKeys = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  // Get user subscription to check feature access
  const user = useAuthStore((state) => state.user);

  // Check if user has merge keys feature - must be explicitly true
  const hasMergeKeysFeature = user?.subscription?.features?.mergeKeys === true;

  // Local UI state
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || "",
  );
  const [viewMode, setViewMode] = useState("grid");
  const ITEMS_PER_PAGE = 12;

  // Get filter values from URL
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const currentSearch = searchParams.get("search") || "";

  // Field type options
  const fieldTypes = [
    { value: "signature", label: t("mergeKeys.types.signature") },
    { value: "initials", label: t("mergeKeys.types.initials") },
    { value: "date", label: t("mergeKeys.types.date") },
    { value: "text", label: t("mergeKeys.types.text") },
    { value: "name", label: t("mergeKeys.types.name") },
    { value: "email", label: t("mergeKeys.types.email") },
    { value: "checkbox", label: t("mergeKeys.types.checkbox") },
  ];

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);

  // Form state
  const [keyName, setKeyName] = useState("");
  const [keyFieldType, setKeyFieldType] = useState("text");
  const [keyDescription, setKeyDescription] = useState("");
  const [keyDefault, setKeyDefault] = useState("");

  // Test document state (text mode)
  const [testInput, setTestInput] = useState("");
  const [testOutput, setTestOutput] = useState("");
  const [testValues, setTestValues] = useState({});

  // Test document state (PDF mode)
  const [testFile, setTestFile] = useState(null);
  const [pdfResults, setPdfResults] = useState(null);
  const [pdfError, setPdfError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  // ============================================
  // TanStack Query: Fetch Merge Keys (only if feature enabled)
  // ============================================
  const { data: mergeKeysData, isLoading: loading } = useQuery({
    queryKey: ["merge-keys", currentPage, currentSearch],
    queryFn: async () => {
      const res = await mergeKeyApi.getAll(
        currentPage,
        ITEMS_PER_PAGE,
        currentSearch,
      );
      // API returns { data: { definitions: [...], pagination: {...} } }
      const data = res.data?.data || res.data;
      return {
        definitions: data?.definitions || [],
        pagination: data?.pagination || {
          page: 1,
          limit: ITEMS_PER_PAGE,
          total: data?.definitions?.length || 0,
          totalPages: 1,
        },
      };
    },
    placeholderData: (previousData) => previousData,
    enabled: hasMergeKeysFeature, // Don't fetch if feature is disabled
  });

  const mergeKeys = mergeKeysData?.definitions || [];
  const pagination = mergeKeysData?.pagination || {
    page: 1,
    limit: ITEMS_PER_PAGE,
    total: 0,
  };
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // ============================================
  // Mutations
  // ============================================
  const createMutation = useMutation({
    mutationFn: (data) => mergeKeyApi.create(data),
    onSuccess: () => {
      toast.success(t("mergeKeys.created"));
      setShowCreateDialog(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["merge-keys"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error?.message || t("errors.generic"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => mergeKeyApi.update(id, data),
    onSuccess: () => {
      toast.success(t("mergeKeys.updated"));
      setShowEditDialog(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["merge-keys"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error?.message || t("errors.generic"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => mergeKeyApi.delete(id),
    onSuccess: () => {
      toast.success(t("mergeKeys.deleted"));
      setShowDeleteDialog(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ["merge-keys"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error?.message || t("errors.generic"));
    },
  });

  const resetToDefaultsMutation = useMutation({
    mutationFn: () => mergeKeyApi.resetToDefaults(),
    onSuccess: () => {
      toast.success(t("mergeKeys.restored"));
      queryClient.invalidateQueries({ queryKey: ["merge-keys"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error?.message || t("errors.generic"));
    },
  });

  const testPdfMutation = useMutation({
    mutationFn: (file) => mergeKeyApi.testDetection(file),
    onSuccess: (response) => {
      const data = response.data?.data || response.data;
      setPdfResults(data);
      setPdfError("");
    },
    onError: (err) => {
      console.error("PDF test failed:", err);
      setPdfError(
        err.response?.data?.error?.message || t("mergeKeys.testError"),
      );
      setPdfResults(null);
    },
  });

  // Check if any mutation is pending
  const submitting =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    resetToDefaultsMutation.isPending;

  // ============================================
  // Event Handlers
  // ============================================
  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchInput) {
      params.set("search", searchInput);
    } else {
      params.delete("search");
    }
    params.delete("page"); // Reset to page 1 on new search
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearchParams({});
  };

  const resetForm = () => {
    setKeyName("");
    setKeyFieldType("text");
    setKeyDescription("");
    setKeyDefault("");
    setSelectedKey(null);
  };

  const handleCreateKey = (e) => {
    e.preventDefault();
    if (!keyName.trim()) return;

    createMutation.mutate({
      key: keyName.toUpperCase().replace(/\s+/g, "_"),
      fieldType: keyFieldType,
      description: keyDescription,
      defaultValue: keyDefault,
    });
  };

  const handleEditKey = (e) => {
    e.preventDefault();
    if (!selectedKey || !keyName.trim()) return;

    updateMutation.mutate({
      id: selectedKey.id,
      data: {
        key: keyName.toUpperCase().replace(/\s+/g, "_"),
        fieldType: keyFieldType,
        description: keyDescription,
        defaultValue: keyDefault,
      },
    });
  };

  const handleDeleteKey = () => {
    if (!selectedKey) return;
    deleteMutation.mutate(selectedKey.id);
  };

  const handleResetToDefaults = () => {
    resetToDefaultsMutation.mutate();
  };

  const openEditDialog = (key) => {
    setSelectedKey(key);
    setKeyName(key.key || key.keyPattern || "");
    setKeyFieldType(key.fieldType || "text");
    setKeyDescription(key.description || "");
    setKeyDefault(key.defaultValue || "");
    setShowEditDialog(true);
  };

  const openDeleteDialog = (key) => {
    setSelectedKey(key);
    setShowDeleteDialog(true);
  };

  const openTestDialog = () => {
    // Initialize test values from merge keys with default values
    const values = {};
    mergeKeys.forEach((mk) => {
      const keyValue = mk.key || mk.keyPattern || "";
      values[keyValue] = mk.defaultValue || "";
    });
    setTestValues(values);
    setTestInput(
      'Hello {{RECIPIENT_NAME}},\n\nYour document "{{DOCUMENT_TITLE}}" is ready for signing.\n\nBest regards,\n{{SENDER_NAME}}',
    );
    setTestOutput("");
    // Reset PDF state
    setTestFile(null);
    setPdfResults(null);
    setPdfError("");
    setShowTestDialog(true);
  };

  const handleTestDocument = () => {
    let output = testInput;
    Object.entries(testValues).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, "gi");
      output = output.replace(regex, value || `{{${key}}}`);
    });
    setTestOutput(output);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error(t("mergeKeys.pdfOnly"));
        return;
      }
      setTestFile(file);
      setPdfResults(null);
      setPdfError("");
    }
  };

  const handleTestPdf = () => {
    if (!testFile) return;
    testPdfMutation.mutate(testFile);
  };

  const clearPdfFile = () => {
    setTestFile(null);
    setPdfResults(null);
    setPdfError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error(t("mergeKeys.pdfOnly"));
        return;
      }
      setTestFile(file);
      setPdfResults(null);
      setPdfError("");
    }
  };

  const renderContent = () => {
    if (mergeKeys.length === 0) {
      return (
        <EmptyState
          onCreateClick={() => setShowCreateDialog(true)}
          hasSearch={!!currentSearch}
        />
      );
    }
    return viewMode === "grid" ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {mergeKeys.map((key) => (
          <MergeKeyCard
            key={key.id}
            mergeKey={key}
            onEdit={openEditDialog}
            onDelete={openDeleteDialog}
          />
        ))}
      </div>
    ) : (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("mergeKeys.key")}</TableHead>
              <TableHead>{t("mergeKeys.fieldType")}</TableHead>
              <TableHead>{t("mergeKeys.description")}</TableHead>
              <TableHead>{t("mergeKeys.defaultValue")}</TableHead>
              <TableHead className="w-12.5" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {mergeKeys.map((key) => (
              <MergeKeyListRow
                key={key.id}
                mergeKey={key}
                onEdit={openEditDialog}
                onDelete={openDeleteDialog}
              />
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  if (loading && !mergeKeysData) {
    return <MergeKeysSkeleton />;
  }

  // ============================================
  // Feature Check - Show upgrade message if merge keys not available
  // ============================================

  if (!hasMergeKeysFeature) {
    return <UpgradeRequired />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("mergeKeys.title")}
          </h1>
          <p className="text-muted-foreground">{t("mergeKeys.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex items-center border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 px-2"
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" onClick={openTestDialog}>
            <FileText className="mr-2 h-4 w-4" />
            {t("mergeKeys.testDocument")}
          </Button>
          <Button
            variant="outline"
            onClick={handleResetToDefaults}
            disabled={submitting}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {t("mergeKeys.restoreDefaults")}
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("mergeKeys.createKey")}
          </Button>
        </div>
      </div>

      {/* Info card */}
      <Card className="bg-muted/50">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            {t("mergeKeys.infoText")}
          </p>
        </CardContent>
      </Card>

      {/* Structured tags help */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t("mergeKeys.structured.title")}
          </CardTitle>
          <CardDescription>
            {t("mergeKeys.structured.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">
              {t("mergeKeys.structured.syntax")}:
            </span>
            <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
              {"[type|req/opt|signer<n>|Label|Name]"}
            </code>
          </div>
          <div className="space-y-2 text-sm">
            {[
              {
                tag: "[sig|req|signer0|Signature|Signature1]",
                desc: t("mergeKeys.structured.example1"),
              },
              {
                tag: "[sig|req|signer0|Signature|Signature2]",
                desc: t("mergeKeys.structured.example2"),
              },
              {
                tag: "[text|opt|signer1|Phone number|Phone1]",
                desc: t("mergeKeys.structured.example3"),
              },
            ].map(({ tag, desc }) => (
              <div
                key={tag}
                className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3"
              >
                <code className="bg-muted px-1.5 py-0.5 rounded text-xs whitespace-nowrap w-fit">
                  {tag}
                </code>
                <span className="text-muted-foreground">{desc}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>{t("mergeKeys.structured.note1")}</p>
            <p>{t("mergeKeys.structured.note2")}</p>
            <p>{t("mergeKeys.structured.note3")}</p>
            <p>{t("mergeKeys.structured.note4")}</p>
          </div>
        </CardContent>
      </Card>

      {/* Search bar */}
      <div className="flex items-center gap-4">
        <form onSubmit={handleSearch} className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("mergeKeys.searchPlaceholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </form>
        {currentSearch && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters}>
            {t("common.clear")}
          </Button>
        )}
        {pagination.total > 0 && (
          <p className="text-sm text-muted-foreground">
            {t("mergeKeys.totalKeys", { count: pagination.total })}
          </p>
        )}
      </div>

      {/* Merge keys grid/list */}
      {renderContent()}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("mergeKeys.createKey")}</DialogTitle>
            <DialogDescription>
              {t("mergeKeys.createKeyDesc")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateKey}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="keyName">{t("mergeKeys.keyName")}</Label>
                <Input
                  id="keyName"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="e.g., RECIPIENT_NAME"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {t("mergeKeys.keyNameHint", {
                    key:
                      keyName.toUpperCase().replace(/\s+/g, "_") || "KEY_NAME",
                  })}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="keyFieldType">{t("mergeKeys.fieldType")}</Label>
                <Select value={keyFieldType} onValueChange={setKeyFieldType}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("mergeKeys.selectFieldType")} />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t("mergeKeys.fieldTypeHint")}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="keyDescription">
                  {t("mergeKeys.description")}
                </Label>
                <Input
                  id="keyDescription"
                  value={keyDescription}
                  onChange={(e) => setKeyDescription(e.target.value)}
                  placeholder={t("mergeKeys.descriptionPlaceholder")}
                />
              </div>
              {keyFieldType === "text" && (
                <div className="space-y-2">
                  <Label htmlFor="keyDefault">
                    {t("mergeKeys.defaultValue")}
                  </Label>
                  <Input
                    id="keyDefault"
                    value={keyDefault}
                    onChange={(e) => setKeyDefault(e.target.value)}
                    placeholder={t("mergeKeys.defaultValuePlaceholder")}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || !keyName.trim()}
              >
                {createMutation.isPending
                  ? t("common.creating")
                  : t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("mergeKeys.editKey")}</DialogTitle>
            <DialogDescription>{t("mergeKeys.editKeyDesc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditKey}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="editKeyName">{t("mergeKeys.keyName")}</Label>
                <Input
                  id="editKeyName"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editKeyFieldType">
                  {t("mergeKeys.fieldType")}
                </Label>
                <Select value={keyFieldType} onValueChange={setKeyFieldType}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("mergeKeys.selectFieldType")} />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="editKeyDescription">
                  {t("mergeKeys.description")}
                </Label>
                <Input
                  id="editKeyDescription"
                  value={keyDescription}
                  onChange={(e) => setKeyDescription(e.target.value)}
                />
              </div>
              {keyFieldType === "text" && (
                <div className="space-y-2">
                  <Label htmlFor="editKeyDefault">
                    {t("mergeKeys.defaultValue")}
                  </Label>
                  <Input
                    id="editKeyDefault"
                    value={keyDefault}
                    onChange={(e) => setKeyDefault(e.target.value)}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending || !keyName.trim()}
              >
                {updateMutation.isPending
                  ? t("common.saving")
                  : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("mergeKeys.deleteKey")}</DialogTitle>
            <DialogDescription>
              {t("mergeKeys.deleteConfirm", {
                key: selectedKey?.key || selectedKey?.keyPattern,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteKey}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? t("common.deleting")
                : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Document Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("mergeKeys.testDocument")}</DialogTitle>
            <DialogDescription>
              {t("mergeKeys.testDocumentDesc")}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="text" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="text">
                <FileText className="mr-2 h-4 w-4" />
                {t("mergeKeys.textTest")}
              </TabsTrigger>
              <TabsTrigger value="pdf">
                <Upload className="mr-2 h-4 w-4" />
                {t("mergeKeys.pdfTest")}
              </TabsTrigger>
            </TabsList>

            {/* Text Test Tab */}
            <TabsContent value="text" className="space-y-4 mt-4">
              {/* Merge key values */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  {t("mergeKeys.testValues")}
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {mergeKeys.map((mk) => {
                    const keyValue = mk.key || mk.keyPattern || "";
                    return (
                      <div key={mk.id} className="space-y-1">
                        <Label
                          htmlFor={`test-${keyValue}`}
                          className="text-xs text-muted-foreground"
                        >
                          {`{{${keyValue}}}`}
                        </Label>
                        <Input
                          id={`test-${keyValue}`}
                          value={testValues[keyValue] || ""}
                          onChange={(e) =>
                            setTestValues((prev) => ({
                              ...prev,
                              [keyValue]: e.target.value,
                            }))
                          }
                          placeholder={mk.defaultValue || keyValue}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Input text */}
              <div className="space-y-2">
                <Label htmlFor="testInput">{t("mergeKeys.inputText")}</Label>
                <Textarea
                  id="testInput"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder={t("placeholders.enterMergeKeys")}
                  rows={5}
                  className="font-mono text-sm"
                />
              </div>

              {/* Process button */}
              <Button onClick={handleTestDocument} className="w-full">
                {t("mergeKeys.processText")}
              </Button>

              {/* Output text */}
              {testOutput && (
                <div className="space-y-2">
                  <Label>{t("mergeKeys.outputText")}</Label>
                  <div className="bg-muted rounded-md p-4 font-mono text-sm whitespace-pre-wrap">
                    {testOutput}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* PDF Test Tab */}
            <TabsContent value="pdf" className="space-y-4 mt-4">
              {/* File upload area */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  {t("mergeKeys.uploadPdf")}
                </Label>

                {!testFile ? (
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                      isDragging
                        ? "border-primary bg-primary/5"
                        : "hover:border-primary/50"
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <Upload
                      className={`mx-auto h-10 w-10 mb-3 ${
                        isDragging ? "text-primary" : "text-muted-foreground"
                      }`}
                    />
                    <p className="text-sm text-muted-foreground mb-1">
                      {t("mergeKeys.dropPdf")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("mergeKeys.pdfOnly")}
                    </p>
                    <input
                      ref={fileInputRef}
                      aria-label="Upload a PDF file"
                      type="file"
                      accept=".pdf,application/pdf"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                  </div>
                ) : (
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <File className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{testFile.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(testFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={clearPdfFile}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Scan button */}
              <Button
                onClick={handleTestPdf}
                className="w-full"
                disabled={!testFile || testPdfMutation.isPending}
              >
                {testPdfMutation.isPending
                  ? t("mergeKeys.scanning")
                  : t("mergeKeys.scanPdf")}
              </Button>

              {/* Error message */}
              {pdfError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {pdfError}
                </div>
              )}

              {/* Results */}
              {pdfResults && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-700 dark:text-green-400 text-sm">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    {t("mergeKeys.foundKeys", {
                      count: pdfResults.summary?.total || 0,
                    })}
                    {pdfResults.summary?.signers?.length > 0 && (
                      <span className="ml-2">
                        ({t("mergeKeys.signers")}:{" "}
                        {pdfResults.summary.signers.join(", ")})
                      </span>
                    )}
                  </div>

                  {/* Detected keys table */}
                  {pdfResults.detected?.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">
                              {t("mergeKeys.keyPattern")}
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              {t("mergeKeys.fieldType")}
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              {t("mergeKeys.signer")}
                            </th>
                            <th className="px-4 py-2 text-left font-medium">
                              {t("mergeKeys.page")}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {pdfResults.detected.map((item) => (
                            <tr
                              key={generateUUID()}
                              className="hover:bg-muted/50"
                            >
                              <td className="px-4 py-2">
                                <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                                  {item.matchedText || item.key}
                                </code>
                              </td>
                              <td className="px-4 py-2">
                                <Badge variant="outline">
                                  {item.fieldType}
                                </Badge>
                              </td>
                              <td className="px-4 py-2">
                                {item.signerNumber || "-"}
                              </td>
                              <td className="px-4 py-2">{item.page || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* No keys found */}
                  {pdfResults.detected?.length === 0 && (
                    <div className="text-center py-6 text-muted-foreground">
                      <Braces className="mx-auto h-10 w-10 mb-2 opacity-50" />
                      <p>{t("mergeKeys.noKeysFound")}</p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MergeKeys;
